import React from 'react';
import PropTypes from 'prop-types';

const IncludedRelationshipContext = React.createContext(undefined);

// Actions
const RELATIONSHIP_REFRESH_STARTED = 'relationship-started';
const RELATIONSHIP_REFRESH_SUCCEEDED = 'relationship-succeeded';
const RELATIONSHIP_REFRESH_FAILED = 'relationship-refresh-failed';

const SET_SHOULD_REFRESH_EVERYTHING_ON_RELATIONSHIP_REFRESH = 'set-should-refresh-everything-on-relationship-refresh';

const init = () => {
  return {
    isInitializing: true,
    relationshipCollection: null,
    isRelationshipRefreshing: false,
    relationshipRefreshError: null,
    setShouldRefreshEverythingOnRelationshipRefresh: false,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case RELATIONSHIP_REFRESH_STARTED: {
      return {
        ...state,
        isRelationshipRefreshing: true,
        relationshipRefreshError: null,
      };
    }
    case RELATIONSHIP_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isRelationshipRefreshing: false,
        relationshipRefreshError: null,
        relationshipCollection: action.relationshipCollection,
        shouldRefreshEverythingOnRelationshipRefresh: false,
      };
    }
    case RELATIONSHIP_REFRESH_FAILED: {
      return {
        ...state,
        isRelationshipRefreshing: false,
        relationshipRefreshError: action.relationshipRefreshError,
        shouldRefreshEverythingOnRelationshipRefresh: false,
      };
    }
    case SET_SHOULD_REFRESH_EVERYTHING_ON_RELATIONSHIP_REFRESH: {
      return {
        ...state,
        shouldRefreshEverythingOnRelationshipRefresh: action.shouldRefreshEverythingOnRelationshipRefresh,
      };
    }
    default: {
      throw new Error(`Unhandled IncludedRelationshipContext action type: ${action.type}`);
    }
  }
};

const IncludedRelationshipProvider = ({
  children,
  relationship,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const setShouldRefreshEverythingOnRelationshipRefresh = (shouldRefreshEverythingOnRelationshipRefresh) => {
    dispatch({ type: SET_SHOULD_REFRESH_EVERYTHING_ON_RELATIONSHIP_REFRESH, shouldRefreshEverythingOnRelationshipRefresh });
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: RELATIONSHIP_REFRESH_STARTED });

        const coll = await relationship.getRelated();

        dispatch({ type: RELATIONSHIP_REFRESH_SUCCEEDED, relationshipCollection: coll });
      } catch (err) {
        dispatch({ type: RELATIONSHIP_REFRESH_FAILED, relationshipRefreshError: err });
      }
    };

    fetchData();
  }, [relationship.etag()]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: !state.relationshipCollection,
      relationshipCollection: state.relationshipCollection,
      isRelationshipRefreshing: state.isRelationshipRefreshing,
      relationshipRefreshError: state.relationshipRefreshError,
      shouldRefreshEverythingOnRelationshipRefresh: state.shouldRefreshEverythingOnRelationshipRefresh,
      setShouldRefreshEverythingOnRelationshipRefresh,
    };
  }, [state]);

  return (
    <IncludedRelationshipContext.Provider value={memoizedValue}>
      {children}
    </IncludedRelationshipContext.Provider>
  );
};

// Use IncludedRelationship State Context
const useIncludedRelationshipContext = () => {
  const context = React.useContext(IncludedRelationshipContext);
  if (context === undefined) {
    throw new Error('useIncludedRelationshipContext must be used within a IncludedRelationshipProvider');
  }
  return context;
};

IncludedRelationshipProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  relationship: PropTypes.object.isRequired,
};

export {
  IncludedRelationshipProvider,
  useIncludedRelationshipContext,
};
